/**
 * W2 — Hole plan designer. Authors the drilled sections of a plan version:
 * section × hole diameter × MD interval × DOL × target cost/duration, for
 * drilling and sidetrack events. An editable grid (shared InlineRowGrid) on the
 * left, a telescoping hand-SVG well schematic on the right; selecting a row
 * highlights its section in the schematic and vice-versa. Diameter binds to the
 * Oxbo-M bit/hole-size catalog via CatalogPicker. Reads/writes the pApi
 * hole-plan dataset endpoint (MSW-seeded standalone).
 *
 * Built to w2-hole-plan.html on the P3 kit. The hole plan does not apply to
 * light events (pulling / workover) — those render the NA state, matching the
 * seed's SECTION_MATRIX (only P and S carry a hole_plan).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@app/hooks";
import { useLocale } from "@hooks/useLocale";
import {
  useGetEventPlansQuery,
  useGetPlanDatasetsQuery,
  usePutHolePlanMutation,
} from "@api/pApi";
import { useGetResolvedCatalogQuery } from "@api/mApi";
import { QueryState } from "@components/ui/QueryState";
import { InlineRowGrid, type GridColumn } from "@components/input/InlineRowGrid";
import type { HolePlanRow, PlanOut } from "@api/types";
import { enrichEventCode, sectionApplies } from "@lib/eventEnrichment";
import { asArray } from "@lib/asArray";
import { fmtMoney, fmtNum } from "@lib/format";
import { latestPlan, statusClass, STATUS_KEY } from "@features/planFinder/planCatalog";
import { HoleSchematic } from "./holeSchematic";
import "./holePlan.css";

/** Stable per-row key for grid ↔ schematic selection (seq is stable enough). */
const rowKey = (r: HolePlanRow, i: number) => `sec-${r.seq || i}-${i}`;

/** Base MD − Top MD, guarding nulls. */
function computedLength(r: HolePlanRow): number | null {
  if (r.top_md == null || r.base_md == null) return null;
  return r.base_md - r.top_md;
}

function makeEmptyRow(index: number): HolePlanRow {
  return {
    seq: index + 1,
    phase: "",
    section: "",
    hole_diameter: null,
    top_md: null,
    base_md: null,
    length: null,
    dol: null,
    target_cost: null,
    target_duration: null,
  };
}

export function HolePlan() {
  const { t, locale } = useLocale();
  const navigate = useNavigate();

  // ── Resolve plan context (event → latest plan) ────────────────────────────
  // W0 sets activeEventId (P event id) + optionally activePlanId when a plan is
  // opened. From the event's plan list we take the requested version, else the
  // latest — so a deep-link into W2 still lands on the current draft.
  const activeEventId = useAppSelector((s) => s.wellContext.activeEventId);
  const activePlanId = useAppSelector((s) => s.wellContext.activePlanId);

  const { data: plansRaw, isLoading: plansLoading } = useGetEventPlansQuery(activeEventId ?? "", {
    skip: !activeEventId,
  });
  // asArray guards a present-but-non-array body before .find / latestPlan's spread.
  const resolvedPlan = useMemo(() => {
    const plans = asArray<PlanOut>(plansRaw);
    if (activePlanId) return plans.find((p) => p.id === activePlanId) ?? latestPlan(plans);
    return latestPlan(plans);
  }, [activePlanId, plansRaw]);

  const planId = resolvedPlan?.id ?? null;
  const enrich = enrichEventCode(resolvedPlan?.event_code);
  const holePlanApplies = sectionApplies("hole_plan", enrich.letter, enrich.isConventional);

  // ── Datasets (the hole-plan rows) ─────────────────────────────────────────
  const {
    data: datasets,
    isLoading: dsLoading,
    isError: dsError,
    refetch,
  } = useGetPlanDatasetsQuery(planId ?? "", { skip: !planId });
  const [putHolePlan, { isLoading: saving }] = usePutHolePlanMutation();

  // Diameter catalog (Oxbo-M bits / hole-size).
  const { data: holeSizes } = useGetResolvedCatalogQuery({ categoryKey: "hole_sizes" });
  const diaEntries = holeSizes?.entries ?? [];

  // Local editable copy of the rows (committed to the server on Save).
  const [rows, setRows] = useState<HolePlanRow[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    // A real backend can return a present-but-non-array body ({}/{hole_plan:null});
    // asArray guards the .map so it never white-screens (see src/lib/asArray.ts).
    if (datasets) {
      setRows(asArray<HolePlanRow>(datasets.hole_plan).map((r) => ({ ...r })));
      setDirty(false);
    }
  }, [datasets]);

  // Keep length auto-derived whenever top/base change; renumber seq on reorder.
  function applyRows(next: HolePlanRow[]) {
    const normalized = next.map((r, i) => ({
      ...r,
      seq: i + 1,
      length: computedLength(r),
    }));
    setRows(normalized);
    setDirty(true);
  }

  async function save() {
    if (!planId) return;
    await putHolePlan({ planId, rows }).unwrap();
    setDirty(false);
    setSavedAt(new Date().toLocaleTimeString(locale === "en" ? "en-US" : "es-AR"));
  }

  // ── Totals (footer) ───────────────────────────────────────────────────────
  const totals = useMemo(() => {
    let length = 0;
    let dol = 0;
    let cost = 0;
    let maxBase = 0;
    for (const r of rows) {
      length += computedLength(r) ?? 0;
      dol += r.dol ?? 0;
      cost += r.target_cost ?? 0;
      if ((r.base_md ?? 0) > maxBase) maxBase = r.base_md ?? 0;
    }
    return { length, dol, cost, td: maxBase, count: rows.length };
  }, [rows]);

  // Build MD = first section flagged as curve/build/lateral, or ~last third of TD.
  const buildMd = useMemo(() => {
    const curve = rows.find((r) =>
      /curva|build|lateral|horizontal/i.test(`${r.phase} ${r.section}`),
    );
    return curve?.top_md ?? null;
  }, [rows]);

  // ── Grid columns ──────────────────────────────────────────────────────────
  const columns: GridColumn<HolePlanRow>[] = [
    {
      key: "phase",
      label: t("holePlan.col.phase"),
      render: (row, update) => (
        <input
          className="hp-cell"
          value={row.phase}
          aria-label={t("holePlan.col.phase")}
          onChange={(e) => update({ phase: e.target.value })}
        />
      ),
    },
    {
      key: "section",
      label: t("holePlan.col.section"),
      render: (row, _update, i) => {
        const key = rowKey(row, i);
        return (
          <button
            type="button"
            className="hp-secname"
            title={t("holePlan.selectHint")}
            onClick={() => setSelectedKey(key)}
          >
            <span
              className="hp-sec-sw"
              style={{ background: `var(${enrich.letter ? opVarFor(row) : "--op-drilling"})` }}
            />
            {row.section || <span className="dim">{t("holePlan.unnamed")}</span>}
          </button>
        );
      },
    },
    {
      key: "diameter",
      label: t("holePlan.col.diameter"),
      numeric: true,
      render: (row, update) => (
        <div className="hp-dia">
          <input
            className="hp-cell num tab-nums"
            inputMode="decimal"
            value={row.hole_diameter ?? ""}
            aria-label={t("holePlan.col.diameter")}
            onChange={(e) =>
              update({ hole_diameter: e.target.value === "" ? null : Number(e.target.value) })
            }
          />
          {/* catalog select — pick a standard hole size from Oxbo-M */}
          <select
            className="hp-cell"
            aria-label={t("holePlan.diaCatalog")}
            value=""
            title={t("holePlan.diaCatalog")}
            onChange={(e) => {
              const entry = diaEntries.find((x) => x.key === e.target.value);
              const inches = entry?.meta?.["in"];
              if (typeof inches === "number") update({ hole_diameter: inches });
            }}
            style={{ maxWidth: 40, padding: "4px 2px" }}
          >
            <option value="">⌀</option>
            {diaEntries.map((en) => (
              <option key={en.key} value={en.key}>
                {en.label}
              </option>
            ))}
          </select>
        </div>
      ),
    },
    {
      key: "top_md",
      label: t("holePlan.col.topMd"),
      numeric: true,
      render: (row, update) => (
        <input
          className="hp-cell num tab-nums"
          inputMode="decimal"
          value={row.top_md ?? ""}
          aria-label={t("holePlan.col.topMd")}
          onChange={(e) =>
            update({ top_md: e.target.value === "" ? null : Number(e.target.value) })
          }
        />
      ),
    },
    {
      key: "base_md",
      label: t("holePlan.col.baseMd"),
      numeric: true,
      render: (row, update) => (
        <input
          className="hp-cell num tab-nums"
          inputMode="decimal"
          value={row.base_md ?? ""}
          aria-label={t("holePlan.col.baseMd")}
          onChange={(e) =>
            update({ base_md: e.target.value === "" ? null : Number(e.target.value) })
          }
        />
      ),
    },
    {
      key: "length",
      label: t("holePlan.col.length"),
      numeric: true,
      render: (row) => (
        <input
          className="hp-cell num tab-nums"
          readOnly
          tabIndex={-1}
          aria-label={t("holePlan.col.length")}
          value={computedLength(row) ?? ""}
        />
      ),
    },
    {
      key: "dol",
      label: t("holePlan.col.dol"),
      numeric: true,
      render: (row, update) => (
        <input
          className="hp-cell num tab-nums"
          inputMode="decimal"
          value={row.dol ?? ""}
          aria-label={t("holePlan.col.dol")}
          onChange={(e) => update({ dol: e.target.value === "" ? null : Number(e.target.value) })}
        />
      ),
    },
    {
      key: "target_duration",
      label: t("holePlan.col.targetDuration"),
      numeric: true,
      render: (row, update) => (
        <input
          className="hp-cell num tab-nums"
          inputMode="decimal"
          value={row.target_duration ?? ""}
          aria-label={t("holePlan.col.targetDuration")}
          onChange={(e) =>
            update({ target_duration: e.target.value === "" ? null : Number(e.target.value) })
          }
        />
      ),
    },
    {
      key: "target_cost",
      label: t("holePlan.col.targetCost"),
      numeric: true,
      render: (row, update) => (
        <input
          className="hp-cell num tab-nums"
          inputMode="decimal"
          value={row.target_cost ?? ""}
          aria-label={t("holePlan.col.targetCost")}
          onChange={(e) =>
            update({ target_cost: e.target.value === "" ? null : Number(e.target.value) })
          }
        />
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  const headTitle = t("holePlan.title");

  return (
    <div>
      <div className="screen-head">
        <h1>{headTitle}</h1>
        <span className="badge info">{t("holePlan.badge")}</span>
        <div className="actions">
          <button type="button" className="btn" disabled>
            <span className="ic ic-import" aria-hidden="true" /> {t("holePlan.copyPrior")}
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={!planId || !dirty || saving || !holePlanApplies}
          >
            <span className="ic ic-check" aria-hidden="true" />
            {saving ? t("holePlan.saving") : t("holePlan.save")}
          </button>
        </div>
      </div>

      {/* plan context chips */}
      {resolvedPlan && (
        <div className="hp-ctx">
          <span className="chip-ctx">
            <span className="k">{t("holePlan.ctx.well")}</span>
            <span className="mono">{resolvedPlan.well_uwi}</span>
            <span className="dim">·</span>
            <span className="k">{t("holePlan.ctx.event")}</span>
            <span className="mono">
              {enrich.letter ?? "?"} · {resolvedPlan.event_code}
            </span>
            <span className="dim">·</span>
            <span className="k">{t("holePlan.ctx.afe")}</span>
            <span className="mono">{resolvedPlan.afe_no ?? t("common.na")}</span>
          </span>
          <span className={`hp-status-pill ${statusClass(resolvedPlan.status)}`}>
            ● {t(STATUS_KEY[resolvedPlan.status], { n: resolvedPlan.version })}
          </span>
          <span className="hp-verchip">v{resolvedPlan.version}</span>
          {enrich.isConventional === false && (
            <span className="badge crit">{t("conv.unconvention")}</span>
          )}
        </div>
      )}

      <QueryState
        isLoading={plansLoading || dsLoading}
        isError={dsError}
        isEmpty={!plansLoading && !dsLoading && !planId}
        emptyMessage={t("holePlan.noPlan")}
        onRetry={() => void refetch()}
      >
        {!holePlanApplies ? (
          <div className="hp-na">
            <h3>{t("holePlan.na.title")}</h3>
            <p>{t("holePlan.na.body", { code: resolvedPlan?.event_code ?? "" })}</p>
            <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "center" }}>
              <button type="button" className="btn sm" onClick={() => navigate("/program")}>
                {t("holePlan.na.toProgram")}
              </button>
              <button type="button" className="btn sm" onClick={() => navigate("/cost")}>
                {t("holePlan.na.toCost")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="hp-note">
              <span className="hp-qm">i</span>
              <span>{t("holePlan.note")}</span>
            </div>

            {/* KPI chips */}
            <div className="kpis">
              <div className="kpi">
                <div className="v tab-nums">{totals.count}</div>
                <div className="l">{t("holePlan.kpi.sections")}</div>
              </div>
              <div className="kpi">
                <div className="v tab-nums">
                  {fmtNum(totals.td, locale)} <span style={{ fontSize: 12, color: "var(--dim)" }}>m</span>
                </div>
                <div className="l">{t("holePlan.kpi.td")}</div>
              </div>
              <div className="kpi">
                <div className="v tab-nums">
                  {fmtNum(totals.dol, locale, { maximumFractionDigits: 1 })}{" "}
                  <span style={{ fontSize: 12, color: "var(--dim)" }}>d</span>
                </div>
                <div className="l">{t("holePlan.kpi.dol")}</div>
              </div>
              <div className="kpi">
                <div className="v tab-nums">{fmtMoney(totals.cost, locale, { compact: true })}</div>
                <div className="l">{t("holePlan.kpi.cost")}</div>
              </div>
            </div>

            <div className="hp-split">
              {/* LEFT: editable grid */}
              <section className="card" style={{ padding: 16 }}>
                <div
                  className="rowflex"
                  style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}
                >
                  <h2 style={{ margin: 0 }}>{t("holePlan.gridTitle")}</h2>
                  {savedAt && !dirty && (
                    <span className="dim" style={{ fontSize: 12 }}>
                      {t("holePlan.savedAt", { time: savedAt })}
                    </span>
                  )}
                </div>
                <div className="dim" style={{ fontSize: 12, margin: "-2px 0 12px" }}>
                  {t("holePlan.gridHint")}
                </div>

                {/* diameter quick-fill chips (fill the selected row) */}
                <div className="hp-catchips">
                  <span className="dim" style={{ fontSize: 12 }}>
                    {t("holePlan.diaCatalog")}:
                  </span>
                  {diaEntries.map((en) => (
                    <button
                      key={en.key}
                      type="button"
                      className="hp-catchip"
                      disabled={!selectedKey}
                      title={selectedKey ? undefined : t("holePlan.selectFirst")}
                      onClick={() => {
                        const inches = en.meta?.["in"];
                        if (typeof inches !== "number") return;
                        applyRows(
                          rows.map((r, i) =>
                            rowKey(r, i) === selectedKey ? { ...r, hole_diameter: inches } : r,
                          ),
                        );
                      }}
                    >
                      {en.label}
                    </button>
                  ))}
                </div>

                <InlineRowGrid
                  rows={rows}
                  columns={columns}
                  onChange={applyRows}
                  makeEmptyRow={makeEmptyRow}
                  addLabel={t("holePlan.addSection")}
                  emptyLabel={t("holePlan.empty")}
                  footer={
                    <div className="dim" style={{ fontSize: 12, marginTop: 10, display: "flex", gap: 6 }}>
                      <span className="hp-qm">i</span>
                      {t("holePlan.footHint")}
                    </div>
                  }
                />
              </section>

              {/* RIGHT: schematic + legend */}
              <aside className="card hp-schem-card">
                <HoleSchematic
                  rows={rows}
                  rowKey={rowKey}
                  selectedKey={selectedKey}
                  onSelect={setSelectedKey}
                  letter={enrich.letter}
                  buildMd={buildMd}
                  caption={t("holePlan.schematic")}
                  ariaLabel={t("holePlan.schematicAria")}
                />
                <div className="hp-legend">
                  {rows.map((r, i) => {
                    const key = rowKey(r, i);
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`row${key === selectedKey ? " on" : ""}`}
                        onClick={() => setSelectedKey(key)}
                      >
                        <span className="sw" style={{ background: `var(${opVarFor(r)})` }} />
                        <span className="nm">{r.section || r.phase || `#${i + 1}`}</span>
                        <span className="dims tab-nums">
                          {r.hole_diameter != null ? `${r.hole_diameter}″` : "—"} ·{" "}
                          {fmtNum(r.top_md, locale)}–{fmtNum(r.base_md, locale)} m
                        </span>
                      </button>
                    );
                  })}
                  {rows.length === 0 && <span className="dim">{t("holePlan.empty")}</span>}
                </div>
              </aside>
            </div>
          </>
        )}
      </QueryState>
    </div>
  );

  /** Colour a section: curve/lateral use the unconventional hue, others drilling. */
  function opVarFor(r: HolePlanRow): string {
    if (/curva|build|lateral|horizontal/i.test(`${r.phase} ${r.section}`)) return "--op-unconv";
    return enrich.letter === "S" ? "--oxbo-m" : "--op-drilling";
  }
}
